import { Component, inject, Injector, Input, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { KeycloakWrapperService } from '@app/finxone-web-frontend/app/lib/services/keycloak-wrapper-service/keycloak-wrapper.service';
import { WorkflowFormGuard } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-form.guard';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { redirectToPageWithUrl } from '@app/finxone-web-frontend/app/lib/utils/navbar-navigation.utils';
import { logoutAndDisconnectWebSockets } from '@app/finxone-web-frontend/app/Modules/shared/components-library/logout/logout.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  NavBarDesktop,
  NavBarIconStyle,
  ProfileStateModel,
  ThemeConfig,
  baseCssConfigStyle,
  defaultNavBar,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import isSvg from 'is-svg';
import { Observable, Subject, distinctUntilChanged, map, take, takeUntil, tap } from 'rxjs';
import { RemoveAllProgressBarStack } from '../../../lib/actions/progress-bar.action';
import { ConfigService } from '../../../lib/services/config-service/config-service.service';
import { RoleState, RoleStateModel } from '../../../lib/state/role.state';
import { SocketService } from '@app/finxone-web-frontend/app/lib/services/socket-service/socket-service.service';

@Component({
  standalone: false,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-navbar-desktop',
  templateUrl: './navbar-desktop.component.html',
  styleUrls: ['./navbar-desktop.component.scss'],
})
export class NavbarDesktopComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() footerStyles: baseCssConfigStyle;
  @Input() currentUrl = '';
  @Select(RoleState.getRole)
  public role!: Observable<RoleStateModel>;
  public roleInfo: RoleStateModel;
  @Select(ProfileState.getProfile)
  public userProfile$!: Observable<ProfileStateModel>;
  public userProfileInfo: ProfileStateModel;
  public navBarDesktopConfig: NavBarDesktop;
  public iconStyles: NavBarIconStyle;
  public containerStyles: baseCssConfigStyle;
  userName: string;
  visible = false;
  logoName: string;

  activeItems: boolean[] = new Array(50).fill(false);

  private readonly socketService = inject(SocketService);
  private readonly router = inject(Router);
  private readonly configService = inject(ConfigService);
  private readonly store = inject(Store);
  private readonly injector = inject(Injector);
  private readonly keycloakService = inject(KeycloakWrapperService);
  private readonly registry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly iconCacheService = inject(IconCacheService);

  constructor() {
    this.role.pipe(takeUntil(this.destroy$)).subscribe((role) => {
      this.roleInfo = role;
      this.getNavBarUIFromSystemConfig();
    });
  }

  ngOnInit() {
    if (this.currentUrl === '') {
      this.router.events
        .pipe(
          takeUntil(this.destroy$),
          map(() => {
            this.currentUrl = this.router.url;
            this.setActiveItems();
          }),
        )
        .subscribe();
    }
    this.getNavBarUIFromSystemConfig();
    this.userProfile();
    this.getLogoName();
    this.loadCustomerLogoToMatRegistry();
  }

  private setActiveItems() {
    if (this.currentUrl && this.navBarDesktopConfig?.layout) {
      this.activeItems = [];
      this.navBarDesktopConfig?.layout.forEach((item, index) => {
        this.activeItems.push(this.isItemActive(item.itemActiveIn, item.navigateToZone, index));
      });
    }
  }

  private userProfile() {
    this.userProfile$.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe((profile) => {
      if (profile) {
        this.userName = profile.fullName.length
          ? profile.fullName
          : profile.firstName + ' ' + profile.lastName;
      }
    });
  }

  private getLogoName() {
    this.configService
      .getSystemName()
      .pipe(
        map((name) => {
          this.logoName = name;
        }),
      )
      .subscribe();
  }

  public navigateToZone(zoneUrl: string): void {
    // Disable form validation check for navigating to the set page
    WorkflowFormGuard.disableFormValidation();
    this.store.dispatch(new RemoveAllProgressBarStack());
    redirectToPageWithUrl(zoneUrl, this.injector);
  }

  private getNavBarUIFromSystemConfig() {
    this.configService
      .getWidgetsConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => {
        const navBarConfig = config?.navbar[this.roleInfo.role]?.navBarDesktop;
        this.navBarDesktopConfig = navBarConfig ?? defaultNavBar.navBarDesktop;
        this.setActiveItems();
        this.footerStyles = {
          ...this.footerStyles,
          'background-color': this.navBarDesktopConfig?.navBarColor,
          'padding-left':
            this.navBarDesktopConfig?.paddingLeft?.toString() + this.navBarDesktopConfig?.paddingUnit,
          'padding-right':
            this.navBarDesktopConfig?.paddingRight?.toString() + this.navBarDesktopConfig?.paddingUnit,
          'padding-top':
            this.navBarDesktopConfig?.paddingTop?.toString() + this.navBarDesktopConfig?.paddingUnit,
          'padding-bottom':
            this.navBarDesktopConfig?.paddingBottom?.toString() + this.navBarDesktopConfig?.paddingUnit,
          'margin-top':
            this.navBarDesktopConfig?.marginTop?.toString() + this.navBarDesktopConfig?.marginUnit,
          'margin-bottom':
            this.navBarDesktopConfig?.marginBottom?.toString() + this.navBarDesktopConfig?.marginUnit,
          'margin-left':
            this.navBarDesktopConfig?.marginLeft?.toString() + this.navBarDesktopConfig?.marginUnit,
          'margin-right':
            this.navBarDesktopConfig?.marginRight?.toString() + this.navBarDesktopConfig?.marginUnit,
        };
        if (this.navBarDesktopConfig?.menuType === 'vertical') {
          this.footerStyles = {
            ...this.footerStyles,
            width: this.navBarDesktopConfig?.width?.toString() + 'px',
          };
        } else {
          delete this.footerStyles.width;
        }
        this.setIconStyles();
      });
  }

  private setIconStyles() {
    const iconWidth = this.navBarDesktopConfig?.iconSize.split('x')[0].trim() + 'px';
    const iconHeight = this.navBarDesktopConfig?.iconSize.split('x')[1].trim() + 'px';

    this.iconStyles = {
      width: iconWidth,
      height: iconHeight,
    };
  }

  //if activeWhenIn is page, the tab will be active on that exact url, when its set to zone the tab will be active when you are on any page in that zone
  private isItemActive(activeWhenIn: 'page' | 'zone', itemNavigateToZone: string, index: number) {
    //to show an active tab on the navbar customiser page
    if (window.location.href.split('/').pop() === 'navigationbar' && index === 0) {
      return true;
    }
    if (activeWhenIn === 'page' || activeWhenIn === undefined) {
      return this.currentUrl === itemNavigateToZone;
    } else {
      const currentZone = this.currentUrl.substring(1).split('/')[1];
      const itemTargetZone = itemNavigateToZone.substring(1).split('/')[1];
      return currentZone === itemTargetZone;
    }
  }

  public onLogout() {
    this.visible = !this.visible;
  }

  public logout() {
    logoutAndDisconnectWebSockets(this.socketService, this.keycloakService);
  }

  private loadCustomerLogoToMatRegistry() {
    this.configService
      .getTheme()
      .pipe(
        take(1),
        tap((config: ThemeConfig) => {
          let logoSvg = '';
          try {
            logoSvg = window.atob(config?.logo ?? '');
          } catch (error) {
            console.warn('No valid svg logo set, setting empty');
          }

          if (isSvg(logoSvg)) {
            this.registry.addSvgIconLiteral(
              'customer-logo',
              this.domSanitizer.bypassSecurityTrustHtml(logoSvg),
            );
            this.iconCacheService.setIcon('customer-logo', logoSvg);
          }
        }),
      )
      .subscribe();
  }
}
